import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useOutletContext, useSearchParams } from "react-router-dom";
import { Pagination } from "@mui/material";
import styles from "./Category.module.css";
import sharedStyles from "../../styles/SharedLayout.module.css";
import { getCategoryPage } from "../../lib/getCategoryPage";
import CategorySliders from "./components/CategorySliders/CategorySliders";
import ProductGrid from "../../components/ProductGrid/ProductGrid";
import {
  getProductsByCategory,
  getAllProductsForCategoryFilters,
} from "../../utils/meilisearchClient";
import { extractFiltersFromProducts } from "../../utils/filterUtils";
import {
  decodeFiltersFromURL,
  updateURLWithFilters,
} from "../../utils/urlFilters";
import type { CategoryData, MeilisearchProduct } from "../../types-var/product";
import type { SortOption } from "../../utils/meilisearchClient";
import Filters from "../../components/Filters/Filters";
import CategoryPageSkeleton from "./components/CategoryPageSkeleton/CategoryPageSkeleton";
import BlogArticlesCollection from "../../components/Blog/BlogArticlesCollection";
import WhatsAppAnnouncementCard from "../../components/WhatsAppAnnouncementCard";
import CategorySEO from "../../components/SEO/CategorySEO";
import metaPixelCatalogService from "../../services/metaPixelCatalogService";

const CategoryPage: React.FC = () => {
  const { categoryUrl } = useParams<{ categoryUrl: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState<CategoryData | null>(null);
  const [products, setProducts] = useState<MeilisearchProduct[]>([]);
  const [availableFilters, setAvailableFilters] = useState({});
  const [filterStyles, setFilterStyles] = useState({});
  const [filterCounts, setFilterCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSort, setCurrentSort] = useState<SortOption>("popularity:desc");
  const { toggleMiniCart } = useOutletContext<{ toggleMiniCart: () => void }>();
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({});
  const [totalPages, setTotalPages] = useState(1);
  const [totalHits, setTotalHits] = useState(0);
  const [blogArticles, setBlogArticles] = useState<any[]>([]);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const HITS_PER_PAGE = 24;

  const [localPage, setLocalPage] = useState(currentPage);

  // Track previous values to detect actual changes (similar to SearchResults)
  const prevSort = useRef(currentSort);
  const isInitialLoad = useRef(true);

  console.log("🔍 CategoryPage Debug:", {
    currentPage,
    localPage,
    searchParamsPage: searchParams.get("page"),
    categoryUrl,
  });

  useEffect(() => {
    console.log("🔄 Setting localPage from currentPage:", currentPage);
    setLocalPage(currentPage);
  }, [currentPage]);

  // Initialize refs on mount
  useEffect(() => {
    if (isInitialLoad.current) {
      prevSort.current = currentSort;
    }
  }, [currentSort]);

  // Initialize filters from URL parameters when category changes
  useEffect(() => {
    const filtersFromURL = decodeFiltersFromURL(searchParams);
    console.log("🔍 Decoded filters from URL:", filtersFromURL);
    console.log("🔍 URL search params:", searchParams.toString());
    setFilters(filtersFromURL);
  }, [categoryUrl, searchParams]);

  const fetchProducts = useCallback(
    async (
      categoryData: CategoryData,
      currentFilters: typeof filters,
      page: number,
      sort: SortOption
    ) => {
      try {
        const searchOptions: any = {
          sort: [sort],
          page: page,
          hitsPerPage: HITS_PER_PAGE,
        };

        console.log(`🚀 Fetching category products for page: ${page}`);

        let filterConditions: string[] = [];

        // Simplified category filtering - direct ID match only for consistency with SearchResults
        filterConditions.push(`categories.id = ${categoryData.id}`);

        if (Object.keys(currentFilters).length > 0) {
          console.log("🔍 Processing filters:", currentFilters);
          Object.entries(currentFilters)
            .filter(([_, values]) => values.length > 0)
            .forEach(([key, values]) => {
              console.log(
                `🔍 Processing filter key: "${key}" with values:`,
                values
              );
              // Use case-insensitive comparison for category filters
              if (key.toLowerCase() === "categorías") {
                const categoryFilters = values.map((categoryValue) => {
                  try {
                    // Parse the JSON string to extract the actual category ID
                    const categoryData = JSON.parse(categoryValue);
                    console.log("🔍 Parsed category data:", categoryData);
                    return `categories.id = ${categoryData.id}`;
                  } catch (error) {
                    // Fallback: if it's not JSON, treat as direct ID
                    console.log("🔍 Using direct category ID:", categoryValue);
                    return `categories.id = ${categoryValue}`;
                  }
                });
                if (categoryFilters.length > 0) {
                  filterConditions.push(`(${categoryFilters.join(" OR ")})`);
                }
              } else {
                const attributeFilters = values.map((value) => {
                  const cleanValue = cleanFilterValue(key, value);
                  console.log(`🔍 Clean value for ${key}:`, cleanValue);
                  try {
                    const parsed = JSON.parse(cleanValue);
                    console.log(`🔍 Parsed JSON for ${key}:`, parsed);
                    const filter = `(attributes.name = '${key}' AND attributes.values.name = '${parsed.name}')`;
                    console.log(`🔍 Generated filter:`, filter);
                    return filter;
                  } catch {
                    // Use single quotes to avoid quote conflicts in MeiliSearch filter expressions
                    const filter = `(attributes.name = '${key}' AND attributes.values.name = '${cleanValue}')`;
                    console.log(`🔍 Generated filter (non-JSON):`, filter);
                    return filter;
                  }
                });
                if (attributeFilters.length > 0) {
                  filterConditions.push(`(${attributeFilters.join(" OR ")})`);
                }
              }
            });
        }

        searchOptions.filter = filterConditions.join(" AND ");

        console.log("Search filters:", searchOptions.filter);

        if (!categoryUrl) throw new Error("Category URL is undefined");

        const results = await getProductsByCategory(
          {
            id: categoryData.id,
            url: categoryUrl || "",
            level: categoryData.level,
          },
          searchOptions
        );

        return results;
      } catch (error) {
        console.error("Error fetching products:", error);
        throw error;
      }
    },
    [categoryUrl]
  );

  useEffect(() => {
    const fetchCategoryData = async () => {
      if (!categoryUrl) return;
      setLoading(true);
      try {
        const categoryData = await getCategoryPage(categoryUrl);
        setCategory(categoryData);

        // Accept blog_articles from API response
        setBlogArticles(categoryData.blog_articles || []);

        // Always reset to page 1 when loading a new category
        // This is separate from pagination navigation
        setSearchParams(
          (prev) => {
            prev.set("page", "1");
            return prev;
          },
          { replace: true }
        );
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to load category"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryData();
  }, [categoryUrl, setSearchParams]);

  // Smart filter extraction strategy: preserve some context while allowing filter discovery
  const createExtractionFilters = useCallback(
    (currentFilters: typeof filters): typeof filters => {
      // For category pages, we want to show filters relative to the current category context
      // Keep some filters for context but allow broader discovery within the category
      const extractionFilters: typeof filters = {};

      Object.entries(currentFilters).forEach(([key, values]) => {
        // Keep category filters for context (they help narrow down the scope appropriately)
        if (key === "Categorías" && values.length > 0) {
          extractionFilters[key] = values;
        }
        // Remove most attribute filters to allow broader discovery, but keep some important ones
        // You could add logic here to keep certain high-level attribute filters
      });

      return extractionFilters;
    },
    []
  );

  // Separate useEffect to handle initial data loading for a category
  useEffect(() => {
    if (!category) return;
    const loadInitialData = async () => {
      try {
        setLoading(true);
        // Use current filters state instead of empty filters
        const results = await fetchProducts(
          category,
          filters,
          localPage,
          currentSort
        );

        // Fetch ALL products for filter extraction using SMART filter strategy
        // Keep some context filters but allow broader discovery within the category
        const extractionFilters = createExtractionFilters(filters);
        const allProductsForFilters = await getAllProductsForCategoryFilters(
          {
            id: category.id,
            url: categoryUrl || "",
            level: category.level,
          },
          extractionFilters
        );

        const {
          filters: extractedFilters,
          styles,
          counts,
        } = extractFiltersFromProducts(allProductsForFilters);

        setProducts(results.hits);
        setAvailableFilters(extractedFilters);
        setFilterStyles(styles);
        setFilterCounts(counts);
        setTotalPages(results.totalPages);
        setTotalHits(results.totalHits);

        // Track category view with enhanced Meta Pixel catalog data
        if (results.hits.length > 0) {
          metaPixelCatalogService.trackCategoryView(
            category.name || categoryUrl || "Unknown Category",
            results.hits.map((product: any) => ({
              id: product.id,
              name: product.name,
              price:
                product.price?.discount?.newPrice ||
                product.price?.regPrice ||
                0,
              discountPrice: product.price?.discount?.newPrice,
              category: product.category?.name || category.name,
              brand: product.partner?.name || "Unknown",
              sku: product.sku || "",
              stock: product.stock || 0,
              images: product.images || [],
              customizableOptions: product.customizableOptions || [],
              attributes: product.attributes || [],
            }))
          );
        }

        setLoading(false);
      } catch (error) {
        console.error("Error loading initial data:", error);
        setLoading(false);
      }
    };

    loadInitialData();
  }, [
    category,
    currentSort,
    filters,
    localPage,
    fetchProducts,
    categoryUrl,
    createExtractionFilters,
  ]);

  // Utility function to clean filter values by removing attribute name prefix

  const cleanFilterValue = (attributeName: string, filterValue: string) => {
    // Skip cleaning for special filters
    if (attributeName === "Categorías") return filterValue;

    try {
      // Handle JSON values (like colors)
      const parsed = JSON.parse(filterValue);
      if (parsed.name) {
        // Remove attribute name prefix if it exists
        const cleanName = parsed.name.startsWith(attributeName + " ")
          ? parsed.name.substring(attributeName.length + 1)
          : parsed.name;
        return JSON.stringify({ ...parsed, name: cleanName });
      }
      return filterValue;
    } catch {
      // Handle plain string values
      if (filterValue.startsWith(attributeName + " ")) {
        return filterValue.substring(attributeName.length + 1);
      }
      return filterValue;
    }
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    // Update URL with new filters - this will reset page to 1
    updateURLWithFilters(newFilters, searchParams, setSearchParams);
  };

  const handleSortChange = (sortBy: SortOption) => {
    // Skip the reset logic on initial load
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      prevSort.current = sortBy;
      setCurrentSort(sortBy);
      return;
    }

    const sortChanged = prevSort.current !== sortBy;

    if (sortChanged) {
      setCurrentSort(sortBy);
      setSearchParams(
        (prev) => {
          prev.set("page", "1");
          return prev;
        },
        { replace: true }
      );
    }

    // Update ref with current value
    prevSort.current = sortBy;
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    if (_event) {
      _event.preventDefault();
    }

    setLocalPage(page);

    console.log(
      `Setting category page to ${page}, updating URL and fetching new data`
    );

    // Update URL directly instead of using setSearchParams to avoid double effects
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    window.history.pushState({}, "", url.toString());
    window.scrollTo(0, 0);
  };

  console.log("category.content:", category?.content);

  useEffect(() => {
    console.log(`Category page state changed to: ${currentPage}`);
  }, [currentPage]);

  console.log("category.content:", category?.content);

  if (loading) return <CategoryPageSkeleton />;
  if (error) return <div>Error: {error}</div>;
  if (!category) return <div>Category not found</div>;

  return (
    <>
      {category && <CategorySEO category={category} />}
      <div className="container">
        <div className={sharedStyles.pageContainer}>
          <h1 className={sharedStyles.pageTitle}>{category.name}</h1>

          {category.content && (
            <div className={styles.categorySlidersContainer}>
              <CategorySliders content={category.content} />
            </div>
          )}

          <div className={sharedStyles.mainContent}>
            <div className={sharedStyles.filtersColumn}>
              {/* Only show Filters in desktop view - ProductGrid handles mobile */}
              <div className={styles.desktopFiltersOnly}>
                <Filters
                  availableFilters={availableFilters}
                  filters={filters}
                  filterStyles={filterStyles}
                  setFilters={handleFilterChange}
                  filterCounts={filterCounts}
                  currentCategoryLevel={category?.level}
                  isMobile={false}
                />
              </div>
            </div>
            <div className={sharedStyles.productsColumn}>
              <ProductGrid
                products={products}
                toggleMiniCart={toggleMiniCart}
                availableFilters={availableFilters}
                filterStyles={filterStyles}
                filterCounts={filterCounts}
                onSortChange={handleSortChange}
                currentSort={currentSort}
                totalHits={totalHits}
                categoryContext={{
                  id: category.id,
                  url: category.url,
                  level: category.level,
                }}
                filters={filters}
                setFilters={handleFilterChange}
                onClearFilters={() => handleFilterChange({})}
              />
              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <Pagination
                    count={totalPages}
                    page={localPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                    className="customPagination"
                    showFirstButton={totalPages > 5}
                    showLastButton={totalPages > 5}
                    siblingCount={0}
                    boundaryCount={1}
                    hidePrevButton={false}
                    hideNextButton={false}
                  />
                  <div className={styles.paginationInfo}>
                    Pág {localPage} de {totalPages}
                  </div>
                </div>
              )}

              {/* WhatsApp Banner */}
              <WhatsAppAnnouncementCard />
            </div>
          </div>
          {/* --- BLOG ARTICLES SECTION OUTSIDE mainContent --- */}
          {blogArticles.length > 0 && (
            <section className={styles.blogSectionContainer}>
              <div className={styles.blogSectionSeparator} />
              <h2 style={{ fontSize: "1.5rem", marginBottom: "1.5rem" }}>
                Encuentra en nuestro blog
              </h2>
              <BlogArticlesCollection title={null} articles={blogArticles} />
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
